/**
 * Компонент не завершен
 * Экспериментальная копия reactApp/components/SharingWindow/SharingContent/SharingContent.tsx
 * Изменения относительно оригинала помечены комментарием - tempexp_17779
 **/

import config from 'Cloud/config';
import classNames from 'clsx';
import React, { type ForwardedRef, forwardRef, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { isAbNewSharingFolder } from 'reactApp/appHelpers/featuresHelpers';
import {
    type SendSharingContentAnalytics,
    type SharingContentProps,
    EFrom,
    ESharingType,
} from 'reactApp/components/SharingWindow/Sharing.types';
import { useItemById } from 'reactApp/components/SharingWindow/SharingNew/hooks/useItemById.hook';
import { SHARING_VARIANT } from 'reactApp/components/SharingWindow/SharingNew/Weblink/WeblinkList/SharingNewWeblinkList.types';
import type { IPropsWithPopup } from 'reactApp/modules/popup/popup.helpers';
import { isOwnPublic as isOwnPublicSelector } from 'reactApp/modules/public/public.selectors';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { isDomainFolder, isMountedFolder } from 'reactApp/modules/storage/folder.helpers';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { isMountedOrSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { TabMenu } from 'reactApp/ui/Menu/TabMenu';
import { ETabMenuAlign, ETabMenuMod } from 'reactApp/ui/Menu/TabMenu.types';
import { TabMenuItem } from 'reactApp/ui/Menu/TabMenuItem';
import { NameComponent } from 'reactApp/ui/Name/Name';
import { sendXray } from 'reactApp/utils/ga';
import { sendPaymentGa } from 'reactApp/utils/paymentGa';

import { getTitle } from '../Sharing.constants';
import styles from '../SharingNew/SharingNew.css';
import { SharingNewSharingTab } from '../SharingNew/SharingTab/SharingNewSharingTab';
import { SharingNewWeblinkTab } from '../SharingNew/Weblink/SharingNewWeblinkTab';
import { useTabsState } from './utils';

const TABS = {
    [ESharingType.WEBLINK]: {
        name: 'По ссылке',
    },
    [ESharingType.CLOUD_USERS]: {
        name: 'Пользователям облака',
    },
};

export const SharingContentTabs = memo(
    // eslint-disable-next-line complexity, max-lines-per-function
    forwardRef((props: IPropsWithPopup<SharingContentProps>, ref: ForwardedRef<HTMLDivElement>) => {
        const { itemStorage, id, from, onClose, gaSuffix, isPhone, withPromoTooltip, onboardingEnabled, folderName } = props;
        const refRoot = useRef<HTMLDivElement | null>(null);

        const storage = useSelector(getCurrentStorage);
        const { isSharedIncoming, isStart, isStock, isInlineIntegration } = getStorage(storage);
        const isBizUser = useSelector(UserSelectors.isBizUser);

        // tempexp_17779-start
        const { fetchedDataOnMount, activeTab, setActiveTab, setFetchedDataOnMount } = useTabsState({ isSharedIncoming, from });
        // tempexp_17779-end

        const item = useItemById(id, itemStorage);
        const isFolder = item?.isFolder;
        const isMounted = isMountedFolder(item);
        const isSharedOrMouted = useSelector((state: RootState) =>
            isFolder ? isMountedOrSharedFolder(state, item) : ({} as ReturnType<typeof isMountedOrSharedFolder>)
        );
        const isDomainItem: boolean = isDomainFolder(item);

        const isPublic = !!config.get('PUBLIC');
        const isOwnPublic = useSelector((state: RootState) => isPublic && isOwnPublicSelector(state));
        const isAlbum = from === EFrom.ALBUMS || storage === EStorageType.albums;
        const noEdit = isStock ? false : isPublic && !isOwnPublic;

        // tempexp_15661-start
        const abSharing = Boolean(!isPhone && isAbNewSharingFolder && item?.name && (isFolder || isAlbum) && !isMounted);
        const title = getTitle({
            isPublic: isStock ? false : isPublic,
            isMounted,
            name: item?.name,
            isAbNewSharingFolder: abSharing,
            isSharingTabsFeature: true,
        });
        // tempexp_15661-end

        const { availableTabs, showSharing } = useMemo(() => {
            const availableTabs: Partial<typeof TABS> = {};

            if (!isSharedIncoming) {
                availableTabs[ESharingType.WEBLINK] = TABS[ESharingType.WEBLINK];
            }

            const showSharing =
                !isAlbum &&
                isFolder &&
                !isDomainItem &&
                !isStart &&
                (!(isSharedOrMouted.isMounted || isSharedOrMouted.isShared) || item?.id === isSharedOrMouted.folderId) &&
                item?.kind !== 'family' &&
                !isInlineIntegration;

            if (showSharing) {
                availableTabs[ESharingType.CLOUD_USERS] = TABS[ESharingType.CLOUD_USERS];
            }

            return { availableTabs, showSharing };
        }, [
            isAlbum,
            isDomainItem,
            isFolder,
            isInlineIntegration,
            isSharedIncoming,
            isSharedOrMouted.folderId,
            isSharedOrMouted.isMounted,
            isSharedOrMouted.isShared,
            isStart,
            item?.id,
            item?.kind,
        ]);

        const onCloseAccess = useCallback(() => {
            if (storage === EStorageType.sharedLinks) {
                onClose();
            }
        }, [storage, onClose]);

        const sendSharingContentAnalytics = useCallback<SendSharingContentAnalytics>(({ action, label }) => {
            sendPaymentGa({ eventCategory: 'enable-sharing', action, label });
            emitAnalyticEvent(AnalyticEventNames.ENABLE_SHARING_SHOW);
        }, []);

        useEffect(() => {
            sendSharingContentAnalytics({ action: 'show' });
        }, [sendSharingContentAnalytics]);

        useEffect(() => {
            const xrayUserType = isBizUser ? 'biz' : 'b2c';

            sendXray(['sharing-content-tabs-mount', xrayUserType]);
        }, []);

        return (
            <div className={classNames(styles.root, { [styles.root_phone]: isPhone })} ref={refRoot}>
                <div className={classNames(styles.header, styles.sharingTabs)} data-qa-id="sharing-header">
                    <span>{title}</span>
                    <NameComponent name={`«${item?.name}»`} truncate showTooltip />
                </div>
                {/* tempexp_17779-start */}
                {Object.entries(availableTabs).length > 1 && (
                    <TabMenu align={ETabMenuAlign.center} mod={ETabMenuMod.folderSharing} qaId="sharing-tab-menu">
                        {Object.entries(availableTabs).map(([value, { name }]) => {
                            return (
                                <TabMenuItem
                                    key={name}
                                    name={`${name}`}
                                    onClick={(value) => {
                                        setActiveTab(value);
                                        if (value === ESharingType.WEBLINK) {
                                            sendSharingContentAnalytics({ action: 'switch', label: 'click' });
                                        }
                                    }}
                                    value={value}
                                    active={activeTab === value}
                                    disabled={false}
                                    changeHash={false}
                                />
                            );
                        })}
                    </TabMenu>
                )}
                {/* tempexp_17779-end */}
                <div className={styles.content} ref={ref}>
                    {activeTab === ESharingType.WEBLINK && ESharingType.WEBLINK in availableTabs && (
                        // tempexp_17779-start
                        <SharingNewWeblinkTab
                            id={id}
                            onboardingEnabled={onboardingEnabled}
                            // tempexp_17779-end
                            item={item}
                            from={from}
                            isSharedOrMouted={isSharedOrMouted}
                            gaSuffix={gaSuffix}
                            noEdit={noEdit}
                            isOwn={!isPublic || isOwnPublic}
                            isPublic={isPublic}
                            isPhone={isPhone}
                            onUnpublish={onCloseAccess}
                            sendSharingContentAnalytics={sendSharingContentAnalytics}
                            withPromoTooltip={withPromoTooltip}
                            showListSharing={showSharing}
                            // tempexp_17779-start
                            foldSettings
                            // tempexp_17779-end
                            variant={SHARING_VARIANT.tabs}
                            folderName={folderName}
                            fromEditor={props.fromEditor}
                            formProps={props}
                            onCloseModal={onClose}
                        />
                    )}

                    {/* Отключаем фичу 'Пользователям облака' в Тутории*/}
                    {activeTab === ESharingType.CLOUD_USERS && ESharingType.CLOUD_USERS in availableTabs && (
                        // tempexp_17779-start
                        <SharingNewSharingTab
                            fetchedDataOnMount={fetchedDataOnMount}
                            setFetchedDataOnMount={setFetchedDataOnMount}
                            // tempexp_17779-end
                            onClose={onClose}
                            item={item}
                            from={from}
                            isPhone={isPhone}
                            isMounted={isMounted}
                        />
                    )}
                </div>
            </div>
        );
    })
);

SharingContentTabs.displayName = 'SharingContentTabs';
